import { ChevronDown, Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Spinner } from '@/components/ui/spinner'
import { useTranslation } from '@/hooks/useTranslation'
import { TOUR } from '@/lib/product-tour/constants'
import type { BpmnExternalToolbarApi } from './bpmnExternalToolbar'

type BpmnExportMenuProps = Pick<
	BpmnExternalToolbarApi,
	| 'loading'
	| 'exportingFormat'
	| 'onExportPng'
	| 'onExportSvg'
	| 'onExportBpmn'
	| 'onExportVisio'
	| 'onExportPptx'
	| 'projectId'
	| 'analysisId'
> & {
	/** When true, attach product-tour data attributes (hoisted global toolbar). */
	showTourTargets?: boolean
}

export function BpmnExportMenu({
	loading,
	exportingFormat,
	onExportPng,
	onExportSvg,
	onExportBpmn,
	onExportVisio,
	onExportPptx,
	projectId,
	analysisId,
	showTourTargets = false,
}: BpmnExportMenuProps) {
	const { t } = useTranslation()

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button
					variant="outline"
					size="sm"
					disabled={loading || !!exportingFormat}
					aria-label={t('components.bpmnEditor.exportDiagram')}
					{...(showTourTargets ? { 'data-tour': TOUR.bpmnExport } : {})}
				>
					{exportingFormat ? (
						<Spinner className="h-4 w-4" />
					) : (
						<>
							<Download className="mr-1 h-4 w-4" />
							{t('components.bpmnEditor.export')}
							<ChevronDown className="ml-1 h-4 w-4" />
						</>
					)}
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent
				align="end"
				{...(showTourTargets ? { 'data-tour': TOUR.bpmnExportMenu } : {})}
				container={
					document.fullscreenElement instanceof HTMLElement ? document.fullscreenElement : null
				}
			>
				<DropdownMenuItem onClick={() => void onExportPng()} disabled={!!exportingFormat}>
					{t('components.bpmnEditor.exportAsPng')}
				</DropdownMenuItem>
				<DropdownMenuItem onClick={() => void onExportSvg()} disabled={!!exportingFormat}>
					{t('components.bpmnEditor.exportAsSvg')}
				</DropdownMenuItem>
				<DropdownMenuItem onClick={() => void onExportBpmn()} disabled={!!exportingFormat}>
					{t('components.bpmnEditor.exportAsBpmn')}
				</DropdownMenuItem>
				{projectId && analysisId ? (
					<>
						<DropdownMenuItem onClick={() => void onExportVisio()} disabled={!!exportingFormat}>
							{t('components.bpmnEditor.exportAsVisio')}
						</DropdownMenuItem>
						<DropdownMenuItem onClick={() => void onExportPptx()} disabled={!!exportingFormat}>
							{t('components.bpmnEditor.exportAsPowerPoint')}
						</DropdownMenuItem>
					</>
				) : null}
			</DropdownMenuContent>
		</DropdownMenu>
	)
}
